import { getMainHost } from "./index";

/**
 * 富文本编辑器内容提取工具
 * 用于过滤不安全内容，同时保留特殊属性
 *
 * 功能特性：
 * 1. 内容提取 (extractRichContent) - 提取安全的内容和特殊元素
 * 2. URL安全验证 - 只允许安全域名的资源
 * 3. 危险标签移除 - 移除script、style等危险标签
 * 4. 危险属性移除 - 移除onclick、onload等事件属性
 * 5. p标签内容过滤 - 只保留video、img、mention-user和post-link标签
 *
 * 保留的特殊元素：
 * - @用户元素 (.mention-user) - 保留data-user-id和data-username属性
 * - 图片元素 (img) - 保留src和alt属性
 * - 视频元素 (video) - 保留src、data-file-id和data-verify-content属性
 * - 链接元素 (.post-link) - 保留data-title和data-link属性
 *
 * 执行顺序：
 * extractRichContent() - 主入口函数
 *   1.1 decodeHTMLEntities() - 解码HTML实体
 *   1.2 提取@用户信息 (.mention-user)
 *   1.3 提取图片信息 (img)
 *   1.4 提取视频信息 (video)
 *   1.5 提取链接信息 (.post-link)
 *   1.6 sanitizeHtml() - 清理危险标签和属性
 *   1.7 rebuildSafeContent() - 重新构建安全内容
 *   1.8 filterParagraphContent() - 过滤段落内容，保留特殊元素
 *   1.9 最终过滤外部图片
 */

export interface ExtractedContent {
  content: string;
  mentions: Array<{
    userId: string;
    username: string;
    element: string;
  }>;
  images: Array<{
    src: string;
    alt: string;
  }>;
  videos: Array<{
    src: string;
    fileId: string;
    verifyContent: string;
  }>;
  links: Array<{
    text: string;
    url: string;
    element: string;
  }>;
}

/**
 * 从富文本内容中提取安全的内容和特殊元素
 *
 * 执行顺序：
 * 1. 解码HTML实体
 * 2. 提取@用户信息 (.mention-user)
 * 3. 提取图片信息 (img)
 * 4. 提取视频信息 (video)
 * 5. 提取链接信息 (.post-link)
 * 6. 清理危险标签和属性
 * 7. 重新构建安全内容
 * 8. 过滤段落内容，保留特殊元素
 * 9. 最终过滤外部图片
 *
 * @param htmlContent 富文本HTML内容
 * @returns 提取的内容和特殊元素信息
 */
export function extractRichContent(htmlContent: string): ExtractedContent {
  // 步骤1: 解码HTML实体
  const decodedContent = decodeHTMLEntities(htmlContent);

  // 创建临时DOM元素来解析HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = decodedContent;

  const result: ExtractedContent = {
    content: "",
    mentions: [],
    images: [],
    videos: [],
    links: [],
  };

  // 步骤2: 提取@用户信息
  const mentionElements = tempDiv.querySelectorAll(".mention-user");
  mentionElements.forEach((element) => {
    const userId = element.getAttribute("data-user-id") || "";
    const username = element.getAttribute("data-username") || "";
    const mentionText = element.textContent || "";

    result.mentions.push({
      userId,
      username,
      element: `<span class="mention-user" data-user-id="${userId}" data-username="${username}" style="color: #f4b73f; background-color: #f4b73f20; padding: 2px 4px; border-radius: 3px; white-space: nowrap; cursor: pointer;">@${username}</span>`,
    });
  });

  // 步骤3: 提取图片信息
  const imageElements = tempDiv.querySelectorAll("img");
  imageElements.forEach((img) => {
    const src = img.getAttribute("src") || "";
    const alt = img.getAttribute("alt") || "picture";

    // 只保留安全的图片URL
    if (isValidImageUrl(src)) {
      result.images.push({
        src,
        alt,
      });
    } else {
      // 移除不安全的图片元素
      img.remove();
    }
  });

  // 步骤4: 提取视频信息
  const videoElements = tempDiv.querySelectorAll("video");
  videoElements.forEach((video) => {
    // 首先尝试从video标签的src属性获取URL
    let src = video.getAttribute("src") || "";
    const fileId = video.getAttribute("data-file-id") || "";
    const verifyContent = video.getAttribute("data-verify-content") || "";

    // 如果video标签没有src属性，尝试从source标签获取
    if (!src) {
      const sourceElement = video.querySelector("source");
      if (sourceElement) {
        src = sourceElement.getAttribute("src") || "";
      }
    }

    // 只保留安全的视频URL
    if (src && isValidVideoUrl(src)) {
      result.videos.push({
        src,
        fileId,
        verifyContent,
      });
    }
  });

  // 步骤5: 提取链接信息
  const linkElements = tempDiv.querySelectorAll(".post-link");
  linkElements.forEach((element) => {
    const text =
      element.getAttribute("data-title") || element.textContent || "";
    const url = element.getAttribute("data-link") || "";

    if (text && url) {
      result.links.push({
        text,
        url,
        element: `<span class="post-link" data-title="${text}" data-link="${url}" style="color: #4a90e2; background-color: #4a90e220; padding: 2px 4px; border-radius: 3px; white-space: nowrap; cursor: pointer; text-decoration: underline;">${text}</span>`,
      });
    }
  });

  // 步骤6: 清理HTML内容，移除脚本和危险标签
  const cleanContent = sanitizeHtml(tempDiv.innerHTML);

  // 步骤7: 重新构建安全的内容
  result.content = rebuildSafeContent(cleanContent, result);

  // 步骤8: 过滤段落内容，保留特殊元素
  result.content = filterParagraphContent(result.content);

  // 步骤9: 最终过滤外部图片和视频
  const finalTempDiv = document.createElement("div");
  finalTempDiv.innerHTML = result.content;

  // 过滤外部图片
  const allImages = finalTempDiv.querySelectorAll("img");
  allImages.forEach((img) => {
    const src = img.getAttribute("src");
    if (src && !isValidImageUrl(src)) {
      img.remove();
    }
  });

  // 过滤外部视频
  const allVideos = finalTempDiv.querySelectorAll("video");
  allVideos.forEach((video) => {
    // 首先尝试从video标签的src属性获取URL
    let src = video.getAttribute("src") || "";

    // 如果video标签没有src属性，尝试从source标签获取
    if (!src) {
      const sourceElement = video.querySelector("source");
      if (sourceElement) {
        src = sourceElement.getAttribute("src") || "";
      }
    }

    if (src && !isValidVideoUrl(src)) {
      video.remove();
    }
  });

  result.content = finalTempDiv.innerHTML;

  return result;
}

/**
 * 验证图片URL是否安全
 *
 * 验证规则：
 * 1. 必须是HTTPS协议
 * 2. 主域名必须是awselbcombine.com
 * 3. 相对路径认为是安全的
 *
 * @param url 图片URL
 * @returns 是否安全
 */
function isValidImageUrl(url: string): boolean {
  if (!url) return false;

  try {
    // 处理协议相对URL（以//开头的URL）
    let processedUrl = url;
    if (url.startsWith("//")) {
      processedUrl = window.location.protocol + url;
    }

    const urlObj = new URL(processedUrl, window.location.origin);

    // 图片安全主域名
    const imageMainHost = "awselbcombine.com";
    const urlMainHost = urlObj.hostname.split(".").slice(-2).join(".");

    // 允许HTTPS协议的图片且主域名匹配的图片
    return urlObj.protocol === "https:" && urlMainHost === imageMainHost;
  } catch {
    // 如果是相对路径，认为是安全的
    return url.startsWith("/") || url.startsWith("./") || url.startsWith("../");
  }
}

/**
 * 验证视频URL是否安全
 *
 * 验证规则：
 * 1. 必须是HTTPS协议
 * 2. 主域名必须是vod-qcloud.com
 * 3. 相对路径认为是安全的
 *
 * @param url 视频URL
 * @returns 是否安全
 */
function isValidVideoUrl(url: string): boolean {
  if (!url) return false;

  try {
    // 处理协议相对URL（以//开头的URL）
    let processedUrl = url;
    if (url.startsWith("//")) {
      processedUrl = window.location.protocol + url;
    }

    const urlObj = new URL(processedUrl, window.location.origin);

    // 视频安全主域名
    const videoMainHost = "vod-qcloud.com";
    const urlMainHost = urlObj.hostname.split(".").slice(-2).join(".");

    // 允许HTTPS协议的视频且主域名匹配的视频
    return urlObj.protocol === "https:" && urlMainHost === videoMainHost;
  } catch {
    // 如果是相对路径，认为是安全的
    return url.startsWith("/") || url.startsWith("./") || url.startsWith("../");
  }
}

/**
 * 过滤外部图片的函数
 *
 * 执行顺序：
 * 1. 查找所有img标签
 * 2. 验证每个图片URL的安全性
 * 3. 移除不安全的图片（非HTTPS或外部域名）
 * 4. 移除没有src属性的图片
 *
 * @param content HTML内容
 * @returns 过滤后的HTML内容
 */
export function filterExternalImages(content: string): string {
  // 步骤1: 创建临时元素来处理HTML内容
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  // 步骤2: 查找所有img标签
  const imgElements = tempDiv.querySelectorAll("img");
  imgElements.forEach((img) => {
    const src = img.getAttribute("src");
    if (src) {
      // 步骤3: 验证图片URL安全性
      try {
        // 处理协议相对URL（以//开头的URL）
        let processedUrl = src;
        if (src.startsWith("//")) {
          processedUrl = window.location.protocol + src;
        }

        const url = new URL(processedUrl, window.location.origin);
        // 图片安全主域名
        const imageMainHost = "awselbcombine.com";
        const urlMainHost = url.hostname.split(".").slice(-2).join(".");

        // 只允许HTTPS协议且主域名匹配的图片
        if (url.protocol !== "https:" || urlMainHost !== imageMainHost) {
          img.remove();
        }
      } catch (error) {
        // 如果URL解析失败，也移除该图片
        img.remove();
      }
    } else {
      // 步骤4: 移除没有src属性的图片
      img.remove();
    }
  });

  return tempDiv.innerHTML;
}

/**
 * 从HTML内容中提取纯文本，保留br标签的换行效果
 *
 * 执行顺序：
 * 1. 将br标签替换为换行符，保留换行效果
 * 2. 特殊处理mention-user和post-link元素，只提取文本内容
 * 3. 移除其他HTML标签，只保留文本内容
 * 4. 统一换行符，清理空白字符
 *
 * @param content HTML内容
 * @returns 纯文本内容
 */
export function extractPlainTextFromHtml(content: string): string {
  // 步骤1: 创建临时元素来处理HTML内容
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  // 步骤2: 将br标签替换为换行符，保留换行效果
  const brElements = tempDiv.querySelectorAll("br");
  brElements.forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  // 步骤3: 移除HTML标签，保留文本内容
  const allElements = tempDiv.querySelectorAll("*");
  allElements.forEach((element) => {
    // 特殊处理mention-user和post-link元素，只提取文本内容
    if (
      element.classList.contains("mention-user") ||
      element.classList.contains("post-link")
    ) {
      const textContent = element.textContent || element.innerText || "";
      if (textContent.trim()) {
        const textNode = document.createTextNode(textContent);
        element.parentNode?.replaceChild(textNode, element);
      } else {
        element.remove();
      }
      return;
    }

    // 处理其他元素
    const textContent = element.textContent || element.innerText || "";
    if (textContent.trim()) {
      const textNode = document.createTextNode(textContent);
      element.parentNode?.replaceChild(textNode, element);
    } else {
      element.remove();
    }
  });

  // 步骤4: 获取纯文本内容并清理格式
  const textContent = tempDiv.textContent || tempDiv.innerText || "";

  // 统一换行符，清理空白字符
  const normalizedText = textContent
    .replace(/\r\n/g, "\n") // 统一换行符
    .replace(/\r/g, "\n") // 统一换行符
    .replace(/[ \t]+/g, " ") // 将连续的空白字符替换为单个空格
    .trim();

  return normalizedText;
}

/**
 * 清理HTML内容，移除危险的标签和属性
 *
 * 执行顺序：
 * 1. 移除script标签
 * 2. 移除style标签
 * 3. 移除危险的事件属性（onclick、onload等）
 *
 * @param html HTML内容
 * @returns 清理后的HTML内容
 */
function sanitizeHtml(html: string): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // 步骤1: 移除脚本标签
  const scripts = tempDiv.querySelectorAll("script");
  scripts.forEach((script) => script.remove());

  // 步骤2: 移除样式标签
  const styles = tempDiv.querySelectorAll("style");
  styles.forEach((style) => style.remove());

  // 步骤3: 移除危险的事件属性
  const allElements = tempDiv.querySelectorAll("*");
  allElements.forEach((element) => {
    const attributes = element.attributes;
    const dangerousAttrs = [
      "onclick",
      "onload",
      "onerror",
      "onmouseover",
      "onmouseout",
      "onfocus",
      "onblur",
      "onchange",
      "onsubmit",
      "onkeydown",
      "onkeyup",
      "onkeypress",
      "oncontextmenu",
      "onbeforeunload",
    ];

    dangerousAttrs.forEach((attr) => {
      if (attributes.getNamedItem(attr)) {
        element.removeAttribute(attr);
      }
    });
  });

  return tempDiv.innerHTML;
}

/**
 * 重新构建安全的内容
 *
 * 执行顺序：
 * 1. 替换@用户元素 (.mention-user)
 * 2. 替换图片元素 (img)
 * 3. 替换视频元素 (video)
 * 4. 替换链接元素 (.post-link)
 *
 * @param cleanHtml 清理后的HTML内容
 * @param extracted 提取的特殊元素信息
 * @returns 重新构建的安全HTML内容
 */
function rebuildSafeContent(
  cleanHtml: string,
  extracted: ExtractedContent
): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = cleanHtml;

  // 步骤1: 替换@用户元素
  extracted.mentions.forEach((mention, index) => {
    const mentionElements = tempDiv.querySelectorAll(".mention-user");
    if (mentionElements[index]) {
      mentionElements[index].outerHTML = mention.element;
    }
  });

  // 步骤2: 替换图片元素
  const imageElements = tempDiv.querySelectorAll("img");
  imageElements.forEach((img, index) => {
    if (extracted.images[index]) {
      img.setAttribute("src", extracted.images[index].src);
      img.setAttribute("alt", extracted.images[index].alt);
      img.setAttribute("style", "max-width: 100%; height: auto;");
    } else {
      // 移除不在安全列表中的图片
      img.remove();
    }
  });

  // 步骤3: 替换视频元素
  const videoElements = tempDiv.querySelectorAll("video");
  videoElements.forEach((video, index) => {
    if (extracted.videos[index]) {
      // 设置video标签的属性
      video.setAttribute("src", extracted.videos[index].src);
      video.setAttribute("data-file-id", extracted.videos[index].fileId);
      video.setAttribute(
        "data-verify-content",
        extracted.videos[index].verifyContent
      );
      video.setAttribute("style", "max-width: 100%; height: auto;");
      video.setAttribute("controls", "");

      // 同时更新source标签（如果存在）
      const sourceElement = video.querySelector("source");
      if (sourceElement) {
        sourceElement.setAttribute("src", extracted.videos[index].src);
        sourceElement.setAttribute("type", "video/mp4");
      }
    } else {
      // 移除不在安全列表中的视频
      video.remove();
    }
  });

  // 步骤4: 替换链接元素
  extracted.links.forEach((link, index) => {
    const linkElements = tempDiv.querySelectorAll(".post-link");
    if (linkElements[index]) {
      linkElements[index].outerHTML = link.element;
    }
  });

  return tempDiv.innerHTML;
}

/**
 * 提交时过滤：保留br换行标签，其他标签转换为纯文本并用p标签包装
 *
 * 执行顺序：
 * 1. 从最内层开始处理元素（避免嵌套问题）
 * 2. 保留 video、img、mention-user、post-link、br 标签
 * 3. 检查元素是否包含特殊子元素（mention-user、post-link、img、video）
 * 4. 如果包含特殊子元素，只处理文本节点，保留特殊元素
 * 5. 其他标签转换为纯文本，用p标签包装
 */
export function filterParagraphContent(htmlContent: string): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  // 步骤1: 从最内层开始处理元素，避免嵌套问题
  const allElements = Array.from(tempDiv.querySelectorAll("*")).reverse();
  const processedElements = new Set();

  allElements.forEach((element) => {
    // 跳过已经处理过的元素
    if (processedElements.has(element)) {
      return;
    }

    // 步骤2: 保留特殊标签（video、img、mention-user、post-link、br）
    if (
      element.tagName.toLowerCase() === "video" ||
      element.tagName.toLowerCase() === "img"
    ) {
      return;
    }

    if (element.className && element.classList.contains("mention-user")) {
      return;
    }

    if (element.className && element.classList.contains("post-link")) {
      return;
    }

    if (element.tagName.toLowerCase() === "br") {
      return;
    }

    // 步骤3: 检查元素是否包含特殊子元素
    const hasMentionUser = element.querySelector(".mention-user");
    const hasPostLink = element.querySelector(".post-link");
    const hasImg = element.querySelector("img");
    const hasVideo = element.querySelector("video");

    if (hasMentionUser || hasPostLink || hasImg || hasVideo) {
      // 如果包含特殊元素，只处理文本节点，保留特殊元素
      const childNodes = Array.from(element.childNodes);
      childNodes.forEach((childNode) => {
        if (childNode.nodeType === Node.TEXT_NODE) {
          const text = childNode.textContent?.trim();
          if (text) {
            const newP = document.createElement("p");
            newP.textContent = text;
            childNode.parentNode?.insertBefore(newP, childNode);
            childNode.remove();
          }
        }
      });
      processedElements.add(element);
      return;
    }

    // 步骤4: 其他标签转换为纯文本，用p标签包装
    const textContent = element.textContent || element.innerText || "";
    if (textContent.trim()) {
      const newP = document.createElement("p");

      // 检查元素内部是否包含br标签
      const brElements = element.querySelectorAll("br");
      if (brElements.length > 0) {
        // 如果有br标签，需要保留换行结构
        let htmlContent = element.innerHTML;

        // 将br标签替换为特殊标记，避免被textContent处理
        htmlContent = htmlContent.replace(/<br\s*\/?>/gi, "{{BR}}");

        // 提取纯文本，但保留br标记
        const tempTextDiv = document.createElement("div");
        tempTextDiv.innerHTML = htmlContent;
        let textWithBr = tempTextDiv.textContent || tempTextDiv.innerText || "";

        // 将br标记转换回br标签
        textWithBr = textWithBr.replace(/\{\{BR\}\}/g, "<br>");

        newP.innerHTML = textWithBr;
      } else {
        // 没有br标签，直接使用textContent
        newP.textContent = textContent;
      }

      element.parentNode?.replaceChild(newP, element);
      processedElements.add(element);
    } else {
      element.remove();
      processedElements.add(element);
    }
  });

  return tempDiv.innerHTML;
}

/**
 * 解码HTML实体
 * 使用textarea元素进行HTML实体解码
 */
function decodeHTMLEntities(text: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
}
